import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabaseClient } from '../../lib/supabase';

export function Breadcrumbs() {
  const location = useLocation();
  const params = useParams();
  const pathnames = location.pathname.split('/').filter(x => x);
  const [projectId, setProjectId] = useState<string | null>(null);

  // If we're on an analysis page, fetch the project_id
  useEffect(() => {
    const fetchProjectId = async () => {
      // Check if we're on an analysis-related page
      const analysisIdIndex = pathnames.findIndex(p => p === 'analyses') + 1;
      const analysisId = pathnames[analysisIdIndex];
      
      if (analysisId && analysisId.length === 36 && analysisId.includes('-')) {
        try {
          const { data, error } = await supabaseClient
            .from('analyses')
            .select('project_id')
            .eq('id', analysisId)
            .single();
          
          if (!error && data) {
            setProjectId(data.project_id);
          }
        } catch (err) {
          console.error('Error fetching project_id for breadcrumbs:', err);
        }
      } else {
        setProjectId(null);
      }
    };

    fetchProjectId();
  }, [location.pathname]);

  // Map of route segments to display names
  const routeNames: { [key: string]: string } = {
    projects: 'Projects',
    analyses: 'Analyses',
    users: 'Users',
    'api-services': 'API Services',
    prompts: 'Prompts',
    'stop-words': 'Stop Words',
    wiki: 'Wiki',
    show: 'View',
    create: 'Create',
    edit: 'Edit',
    editor: 'Editor',
    ai: 'AI Assistant'
  };

  // Skip breadcrumbs for home page
  if (pathnames.length === 0) {
    return null;
  }

  return (
    <nav className="bg-gray-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-10 text-sm">
          <Link to="/" className="text-gray-500 hover:text-gray-700">
            Dashboard
          </Link>
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const displayName = routeNames[name] || name;

            // Skip IDs in the breadcrumb display
            if (name.length === 36 && name.includes('-')) {
              return null;
            }

            // Special handling for "Analyses" link when we have a projectId
            let linkTo = routeTo;
            if (name === 'analyses' && projectId) {
              linkTo = `/projects/show/${projectId}`;
            }

            return (
              <div key={name} className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                {isLast ? (
                  <span className="text-gray-900 font-medium">{displayName}</span>
                ) : (
                  <Link
                    to={linkTo}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {displayName}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}